import { escapeHtmlAttribute } from './helpers'

/**
 * A YouTube iframe costs ~1 MB of player script and ~0.5 s of main thread on a phone, even when nobody
 * plays the video (PageSpeed, article GTA VI, 07/10/2026: 2 players = 2 MB, 1 s of main thread).
 * The facade shows the video thumbnail with a play button and only loads the player on click
 * (plugins/youtube-facade.client.ts for the HTML rendered with v-html, VideoFacade.vue in components).
 */
const YOUTUBE_EMBED =
  /^https:\/\/www\.youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{11})(?:\?([^#]*))?$/

const decodeAttribute = (value: string) =>
  value
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')

const readAttribute = (attributes: string, name: string) => {
  const match = attributes.match(
    new RegExp(`\\s${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, 'i'),
  )
  return match ? decodeAttribute(match[1] ?? match[2] ?? '') : undefined
}

export const parseYoutubeEmbed = (src?: string | null) => {
  const match = src?.trim().match(YOUTUBE_EMBED)
  if (!match) return undefined

  // only keep the player options, the facade adds autoplay itself
  const params = new URLSearchParams(match[2] ?? '')
  params.delete('autoplay')

  return { id: match[1], params: params.toString() }
}

// The thumbnail is shown at the width of the player: hqdefault (480x360) is blurry there. WebP copies in 640
// (sddefault) and 1280 (maxresdefault) px, picked by the browser from the real pixel density. The HD copies do not
// exist for every video: YouTube then answers a 404 (or a 120x90 grey placeholder), and the facade falls back
// to hqdefault, which always exists. fetchpriority=low: Chrome fetches lazy images up to 2500 px below the
// screen, the thumbnail must not compete with what the first paint needs.
export const YOUTUBE_THUMBNAIL_SIZES = '(max-width: 1200px) 100vw, 1200px'

export const youtubeThumbnail = (id: string) => ({
  src: `https://i.ytimg.com/vi_webp/${id}/sddefault.webp`,
  srcset: `https://i.ytimg.com/vi_webp/${id}/sddefault.webp 640w, https://i.ytimg.com/vi_webp/${id}/maxresdefault.webp 1280w`,
})

export const fallbackYoutubeThumbnail = (img: HTMLImageElement, failed = false) => {
  if (img.dataset.ytFallback || !img.complete) return
  // naturalWidth 0 = not loaded yet (lazy): wait for its load or error event
  if (!failed && (img.naturalWidth === 0 || img.naturalWidth > 120)) return

  const id = (img.currentSrc || img.src).match(/\/vi(?:_webp)?\/([A-Za-z0-9_-]{11})\//)?.[1]
  if (!id) return
  img.dataset.ytFallback = '1'
  img.removeAttribute('srcset')
  img.removeAttribute('sizes')
  img.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
}

export const youtubeAutoplayUrl = (id: string, params = '') => {
  const query = new URLSearchParams(params)
  query.set('autoplay', '1')
  return `https://www.youtube.com/embed/${id}?${query.toString()}`
}

/**
 * Replaces the YouTube iframes of an HTML string with a facade button. Class and inline style of the
 * iframe move to the button (the articles position their iframes in a 16:9 wrapper), the other
 * iframes are left as they are. Works on the string, so the same markup is rendered by SSR.
 */
export const youtubeFacadeHtml = (html?: string | null) => {
  if (!html || !html.includes('youtube')) return html ?? ''

  return html.replace(
    /<iframe\b([^>]*)>\s*<\/iframe>/gi,
    (iframe, attributes: string) => {
      const video = parseYoutubeEmbed(readAttribute(attributes, 'src'))
      if (!video) return iframe

      const className = readAttribute(attributes, 'class')
      const style = readAttribute(attributes, 'style')
      const title = readAttribute(attributes, 'title')
      const thumbnail = youtubeThumbnail(video.id)

      return (
        `<button type="button" class="yt-facade${className ? ` ${escapeHtmlAttribute(className)}` : ''}"` +
        (style ? ` style="${escapeHtmlAttribute(style)}"` : '') +
        ` data-yt-id="${video.id}" data-yt-params="${escapeHtmlAttribute(video.params)}"` +
        ` aria-label="${escapeHtmlAttribute(title ? `Lire la vidéo : ${title}` : 'Lire la vidéo')}">` +
        `<img class="yt-facade__thumb" src="${thumbnail.src}" srcset="${thumbnail.srcset}" sizes="${YOUTUBE_THUMBNAIL_SIZES}" alt="" loading="lazy" decoding="async" fetchpriority="low">` +
        '<span class="yt-facade__play" aria-hidden="true"></span>' +
        '</button>'
      )
    },
  )
}

/** YouTube ids of the iframes of an HTML string, in order, without duplicates. */
export const youtubeIdsInHtml = (html?: string | null) => {
  const ids: string[] = []
  for (const [, attributes] of (html ?? '').matchAll(/<iframe\b([^>]*)>/gi)) {
    const id = parseYoutubeEmbed(readAttribute(attributes, 'src'))?.id
    if (id && !ids.includes(id)) ids.push(id)
  }
  return ids
}
